<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container fluid>
        <v-card class="rounded-xl tw-min-h-80">
          <v-card-text v-if="!selectedCompany.isDemo && (userCompany?.isDeveloper || user?.isEngineer)">
            <!-- Breadcrumbs -->
            <v-breadcrumbs :items="breadcrumbItems">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <!-- Page Title -->
            <div class="tw-mx-5 tw-font-bold tw-text-xl tw-text-black tw-tracking-wide tw-mb-6">
              จัดการผลิตภัณฑ์
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="tw-mx-5">
              <v-skeleton-loader type="article, article"></v-skeleton-loader>
            </div>

            <!-- Content -->
            <div v-else class="tw-mx-5">
              <!-- Main Products Section -->
              <div class="tw-mb-8">
                <h2 class="tw-font-bold tw-text-base tw-mb-4 tw-text-gray-800">
                  ผลิตภัณฑ์หลัก
                </h2>
                <v-row class="tw-mx-0">
                  <!-- E-Tax Product -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon-setting-company.svg" alt="E-Tax" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isEtax"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            E-Tax <span :class="['version-badge', versionClass('isEtax')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isEtax')">{{ getVersionText('isEtax') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ระบบจัดการภาษีอิเล็กทรอนิกส์
                      </p>
                    </div>
                  </v-col>

                  <!-- PDF/AR Automation -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon_auto_doc.svg" alt="AR Automation" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isPdfDoc"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            AR Automation <span :class="['version-badge', versionClass('isPdfDoc')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isPdfDoc')">{{ getVersionText('isPdfDoc') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ระบบอัตโนมัติลูกหนี้การค้า (PDF)
                      </p>
                    </div>
                  </v-col>

                  <!-- AP Automation -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon-setting-ai-ap.svg" alt="AP Automation" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isApAutomate"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            AP Automation <span :class="['version-badge', versionClass('isApAutomate')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isApAutomate')">{{ getVersionText('isApAutomate') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ระบบอัตโนมัติเจ้าหนี้การค้า
                      </p>
                    </div>
                  </v-col>

                  <!-- AP Lab/Student Mode -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon-setting-test-ap.svg" alt="AP Lab" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isLab"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            AP Lab/Student Mode <span :class="['version-badge', versionClass('isLab')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isLab')">{{ getVersionText('isLab') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        โหมดห้องปฏิบัติการ/นักเรียน AP
                      </p>

                      <!-- License หมดอายุ — บริษัท Lab สร้างต่อคอร์ส เมื่อเลยวันนี้
                           แล้วผู้ใช้จะเข้าบริษัทไม่ได้ (ทีมงานยังเข้าได้เพื่อต่ออายุ) -->
                      <div v-if="productList.isLab" class="tw-pl-7 tw-mt-3">
                        <v-menu
                          v-model="labExpireMenu"
                          :close-on-content-click="false"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="productList.isLabExpireDate || ''"
                              label="License หมดอายุ"
                              placeholder="ไม่กำหนด"
                              prepend-inner-icon="mdi-calendar-clock"
                              :hint="labExpireHint"
                              persistent-hint
                              :error="isLabLicenseExpired"
                              readonly
                              outlined
                              dense
                              :disabled="saving"
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="productList.isLabExpireDate"
                            no-title
                            color="#6ec39b"
                            @input="labExpireMenu = false"
                          ></v-date-picker>
                        </v-menu>

                        <div class="tw-flex tw-flex-wrap tw-items-center tw-mt-1">
                          <v-btn small text color="primary" :disabled="saving" @click="extendLabLicense(1)">
                            +1 เดือน
                          </v-btn>
                          <v-btn small text color="primary" :disabled="saving" @click="extendLabLicense(3)">
                            +3 เดือน
                          </v-btn>
                          <v-btn small text color="primary" :disabled="saving" @click="extendLabLicense(12)">
                            +1 ปี
                          </v-btn>
                          <v-btn
                            v-if="productList.isLabExpireDate"
                            small
                            text
                            color="grey darken-1"
                            :disabled="saving"
                            @click="productList.isLabExpireDate = null"
                          >
                            ไม่กำหนดวันหมดอายุ
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <!-- Exchange Rate -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/currency-exchange.svg" alt="Exchange Rate" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isExchangeRate"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            Exchange Rate <span :class="['version-badge', versionClass('isExchangeRate')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isExchangeRate')">{{ getVersionText('isExchangeRate') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ระบบจัดการอัตราแลกเปลี่ยน
                      </p>
                    </div>
                  </v-col>

                  <!-- Netsuite Thai Smart Tax -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon-setting-company.svg" alt="NS Thai Smart Tax" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isNsThaiSmartTax"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            NS Thai Smart Tax <span :class="['version-badge', versionClass('isNsThaiSmartTax')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isNsThaiSmartTax')">{{ getVersionText('isNsThaiSmartTax') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        NetSuite ภาษีไทยอัจฉริยะ
                      </p>
                    </div>
                  </v-col>

                  <!-- Topserv -->
                  <!-- <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon-setting-company.svg" alt="Topserv" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isTopserv"
                          label="Topserv"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        ></v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ระบบจัดการ Topserv
                      </p>
                    </div>
                  </v-col> -->

                  <!-- Fixed Asset Count -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/icon-setting-company.svg" alt="Fixed Asset Count" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isFixedAssetCount"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            Fixed Asset Count <span :class="['version-badge', versionClass('isFixedAssetCount')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isFixedAssetCount')">{{ getVersionText('isFixedAssetCount') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ระบบนับสินทรัพย์ถาวร
                      </p>
                    </div>
                  </v-col>

                  <!-- Lao TaxRIS -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2">
                    <div class="product-switch-wrapper">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <img src="/images/icons/lao_tax.jpg" alt="Lao TaxRIS" class="tw-w-5 tw-h-5" />
                        <v-switch
                          v-model="productList.isLaoTaxRis"
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        >
                          <template v-slot:label>
                            Lao TaxRIS <span :class="['version-badge', versionClass('isLaoTaxRis')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isLaoTaxRis')">{{ getVersionText('isLaoTaxRis') }}</span>
                          </template>
                        </v-switch>
                      </div>
                      <p class="tw-text-xs tw-text-gray-500 tw-pl-7 tw-mt-1">
                        ส่งเอกสารกรมสรรพากรลาว
                      </p>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Additional Settings Section -->
              <v-divider class="tw-my-6" v-if="productList.isApAutomate || productList.isNsThaiSmartTax || productList.isEtax"></v-divider>

              <div class="tw-mb-8" v-if="productList.isApAutomate || productList.isNsThaiSmartTax || productList.isEtax">
                <h2 class="tw-font-bold tw-text-base tw-mb-4 tw-text-gray-800">
                  การตั้งค่าเพิ่มเติม
                </h2>
                <v-row class="tw-mx-0">
                  <!-- AP Automation Type -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2" v-if="productList.isApAutomate">
                    <div class="additional-setting-item">
                      <label class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-block tw-font-medium">ประเภท AP Automation</label>
                      <v-select
                        v-model="productList.isApAutomateType"
                        :items="apAutomateTypes"
                        :disabled="saving"
                        outlined
                        dense
                        color="#6ec39b"
                        item-color="#6ec39b"
                        hide-details
                        class="tw-mt-1"
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- E-Tax Type -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2" v-if="productList.isEtax">
                    <div class="additional-setting-item">
                      <label class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-block tw-font-medium">ประเภท E-Tax</label>
                      <v-select
                        v-model="productList.isEtaxType"
                        :items="etaxTypes"
                        :disabled="saving"
                        outlined
                        dense
                        color="#6ec39b"
                        item-color="#6ec39b"
                        hide-details
                        class="tw-mt-1"
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- Netsuite Thai Smart Tax Average -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2" v-if="productList.isNsThaiSmartTax">
                    <div class="additional-setting-item">
                      <label class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-block tw-font-medium">
                        NS Tax Average
                        <span :class="['version-badge', versionClass('isNsThaiSmartTaxAverage')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isNsThaiSmartTaxAverage')">{{ getVersionText('isNsThaiSmartTaxAverage') }}</span>
                      </label>
                      <div class="product-switch-wrapper tw-mt-1">
                        <v-switch
                          v-model="productList.isNsThaiSmartTaxAverage"
                          label=""
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        ></v-switch>

                      </div>
                    </div>
                  </v-col>

                  <!-- Netsuite Thai Smart Tax PND54/PP36 -->
                  <v-col cols="12" sm="6" md="4" class="tw-py-2" v-if="productList.isNsThaiSmartTax">
                    <div class="additional-setting-item">
                      <label class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-block tw-font-medium">
                        ภ.ง.ด.54 / ภ.พ.36
                        <span :class="['version-badge', versionClass('isNsThaiSmartPnd54Pp36')]" @mousedown.prevent.stop @click.prevent.stop="openVersionDialog('isNsThaiSmartPnd54Pp36')">{{ getVersionText('isNsThaiSmartPnd54Pp36') }}</span>
                      </label>
                      <div class="product-switch-wrapper tw-mt-1">
                        <v-switch
                          v-model="productList.isNsThaiSmartPnd54Pp36"
                          label=""
                          color="#6ec39b"
                          :disabled="saving"
                          hide-details
                          class="tw-mt-0"
                        ></v-switch>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Summary Section -->
              <v-divider class="tw-my-6"></v-divider>

              <div class="tw-mb-4">
                <h2 class="tw-font-bold tw-text-base tw-mb-4 tw-text-gray-800">
                  สรุปผลิตภัณฑ์ที่เปิดใช้งาน
                </h2>
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                  <v-chip
                    v-for="key in Object.keys(activeProducts)"
                    :key="key"
                    color="#6ec39b"
                    text-color="white"
                    small
                  >
                    <v-icon left small>mdi-check-circle</v-icon>
                    {{ getProductLabel(key) }}
                  </v-chip>
                  <div v-if="Object.keys(activeProducts).length === 0" class="tw-w-full tw-text-center tw-text-gray-400 tw-py-4">
                    ไม่มีผลิตภัณฑ์ที่เปิดใช้งาน
                  </div>
                </div>
              </div>

              <!-- Save/Cancel Buttons -->
              <div class="tw-mt-8 tw-flex tw-gap-3 tw-justify-end">
                <v-btn
                  outlined
                  color="#6ec39b"
                  @click="cancelChanges"
                  :disabled="!hasChanges || saving"
                  class="tw-px-6 action-button"
                  :class="{ 'button-disabled': !hasChanges || saving }"
                >
                  ยกเลิก
                </v-btn>
                <v-btn
                  color="#6ec39b"
                  dark
                  @click="saveChanges"
                  :disabled="!hasChanges || saving"
                  :loading="saving"
                  class="tw-px-8 action-button"
                  :class="{ 'button-disabled': !hasChanges || saving }"
                >
                  <v-icon left small>mdi-content-save</v-icon>
                  บันทึก
                </v-btn>
              </div>

              <!-- Danger Zone -->
              <v-divider class="tw-my-8" v-if="productList.isEtax"></v-divider>
              <div class="danger-zone" v-if="productList.isEtax">
                <h2 class="tw-font-bold tw-text-base tw-mb-2 danger-zone__title">
                  <v-icon color="#c9242b" small class="tw-mr-1">mdi-alert-octagon-outline</v-icon>
                  ระงับการใช้งานบริษัท
                </h2>
                <p class="tw-text-xs tw-text-gray-600 tw-mb-4">
                  เมื่อระงับบริษัท ผู้ใช้จะไม่สามารถนำเข้าหรือส่งเอกสารได้ทั้งหมด เหมาะสำหรับกรณีบริษัทค้างชำระค่าบริการ
                </p>

                <div class="danger-zone__row">
                  <div>
                    <div class="tw-flex tw-items-center tw-gap-2">
                      <span class="tw-font-medium tw-text-sm">สถานะบริษัท:</span>
                      <v-chip
                        small
                        :color="isCompanySuspended ? '#c9242b' : '#6ec39b'"
                        text-color="white"
                      >
                        <v-icon left small>
                          {{ isCompanySuspended ? 'mdi-lock' : 'mdi-check-circle' }}
                        </v-icon>
                        {{ isCompanySuspended ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน' }}
                      </v-chip>
                    </div>
                    <div v-if="isCompanySuspended" class="tw-text-xs tw-text-gray-600 tw-mt-2">
                      <div v-if="selectedCompany.suspendedReason">
                        <b>เหตุผล:</b> {{ selectedCompany.suspendedReason }}
                      </div>
                      <div v-if="suspendedAtText">
                        <b>ปิดเมื่อ:</b> {{ suspendedAtText }}
                        <span v-if="selectedCompany.suspendedBy"> โดย {{ selectedCompany.suspendedBy }}</span>
                      </div>
                    </div>
                  </div>
                  <v-btn
                    v-if="!isCompanySuspended"
                    outlined
                    color="#c9242b"
                    @click="openSuspendDialog"
                    :disabled="suspending"
                  >
                    <v-icon left small>mdi-lock</v-icon>
                    ปิดการใช้งานบริษัท
                  </v-btn>
                  <v-btn
                    v-else
                    color="#6ec39b"
                    dark
                    @click="openUnsuspendDialog"
                    :disabled="suspending"
                  >
                    <v-icon left small>mdi-lock-open-variant</v-icon>
                    เปิดการใช้งานอีกครั้ง
                  </v-btn>
                </div>
              </div>

              <!-- Suspend Confirm Dialog -->
              <v-dialog v-model="suspendDialog" max-width="480" persistent>
                <v-card class="rounded-lg">
                  <v-card-title class="tw-text-base tw-font-bold">
                    <v-icon color="#c9242b" left>mdi-alert-octagon-outline</v-icon>
                    ยืนยันการปิดบริษัท
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="tw-pt-4">
                    <p class="tw-text-sm tw-text-gray-700 tw-mb-3">
                      บริษัท <b>{{ selectedCompany.nameTH || selectedCompany.name || '-' }}</b> จะไม่สามารถนำเข้าหรือส่งเอกสารได้ จนกว่าจะเปิดใช้งานอีกครั้ง
                    </p>
                    <v-textarea
                      v-model="suspendReason"
                      label="เหตุผลในการปิด (จะบันทึกไว้ในระบบ)"
                      placeholder="เช่น ค้างชำระค่าแพ็คเกจประจำเดือน..."
                      outlined
                      dense
                      auto-grow
                      rows="3"
                      :rules="[v => !!v && v.trim().length > 0 || 'กรุณาระบุเหตุผล']"
                      hide-details="auto"
                      color="#c9242b"
                    ></v-textarea>
                  </v-card-text>
                  <v-card-actions class="tw-px-6 tw-pb-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="suspendDialog = false" :disabled="suspending">
                      ยกเลิก
                    </v-btn>
                    <v-btn
                      color="#c9242b"
                      dark
                      @click="confirmSuspend"
                      :loading="suspending"
                      :disabled="suspending"
                    >
                      ยืนยันปิดบริษัท
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- Unsuspend Confirm Dialog -->
              <v-dialog v-model="unsuspendDialog" max-width="420" persistent>
                <v-card class="rounded-lg">
                  <v-card-title class="tw-text-base tw-font-bold">
                    <v-icon color="#6ec39b" left>mdi-lock-open-variant</v-icon>
                    เปิดการใช้งานบริษัท
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="tw-pt-4 tw-text-sm tw-text-gray-700">
                    ต้องการเปิดการใช้งานบริษัท <b>{{ selectedCompany.nameTH || selectedCompany.name || '-' }}</b> อีกครั้งใช่หรือไม่?
                  </v-card-text>
                  <v-card-actions class="tw-px-6 tw-pb-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="unsuspendDialog = false" :disabled="suspending">
                      ยกเลิก
                    </v-btn>
                    <v-btn color="#6ec39b" dark @click="confirmUnsuspend" :loading="suspending">
                      ยืนยันเปิดใช้งาน
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>

            <!-- Version Bump Dialog -->
            <!-- Version History Dialog -->
            <v-dialog v-model="versionDialog" max-width="480" scrollable>
              <v-card class="rounded-lg">
                <v-card-title class="tw-text-base tw-font-bold tw-flex tw-justify-between tw-items-center">
                  <span>{{ getProductLabel(versionDialogKey) }} — v{{ versionDialogCurrent }}</span>
                  <v-btn icon small @click="versionDialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <div v-if="currentHistory.length > 0" class="history-list">
                    <div
                      v-for="(entry, i) in currentHistory"
                      :key="i"
                      class="history-item"
                    >
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <span class="history-version">v{{ entry.version }}</span>
                        <span class="tw-text-xs tw-text-gray-400">{{ entry.updatedAt }}</span>
                      </div>
                      <div class="tw-text-sm tw-text-gray-700 tw-mt-1" v-if="entry.note">
                        {{ entry.note }}
                      </div>
                      <div class="tw-text-xs tw-text-gray-400 tw-mt-1" v-if="entry.updatedBy">
                        โดย {{ entry.updatedBy }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="tw-text-center tw-text-gray-400 tw-text-sm tw-py-6">
                    ยังไม่มีประวัติ
                  </div>
                </v-card-text>
              </v-card>
            </v-dialog>

            <!-- Success/Error Messages -->
            <v-snackbar
              v-model="snackbar"
              :color="snackbarColor"
              :timeout="3000"
              top
            >
              {{ snackbarMessage }}
              <template v-slot:action="{ attrs }">
                <v-btn
                  text
                  v-bind="attrs"
                  @click="snackbar = false"
                >
                  ปิด
                </v-btn>
              </template>
            </v-snackbar>
          </v-card-text>

          <!-- No Permission State -->
          <div
            v-else
            class="tw-flex tw-items-center tw-justify-center"
            style="height: 200px"
          >
            No Permission
          </div>
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
/* global __VERSION_AUTHORS__ */
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";
import fc from "@/firebaseConfig";
import configVersions from "@/config/product-versions";
import {
  addMonthsToDateString,
  bangkokToday,
  formatLabExpireDate,
  isLabLicenseExpired,
  labLicenseDaysLeft,
} from "@/libs/labLicense";

export default {
  name: "SettingProducts",
  components: {
    Admin,
  },
  data() {
    return {
      loading: true,
      saving: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      hasChanges: false,
      versionDialog: false,
      versionDialogKey: '',
      versionDialogCurrent: '',
      versionDialogNote: '',
      versionHistory: {},
      showHistory: false,
      suspendDialog: false,
      unsuspendDialog: false,
      suspendReason: "",
      suspending: false,
      labExpireMenu: false,
      productList: {
        isEtax: false,
        isEtaxType: 1,
        isPdfDoc: false,
        isApAutomate: false,
        isApAutomateType: 2,
        isExchangeRate: false,
        isNsThaiSmartTax: false,
        isNsThaiSmartTaxAverage: false,
        isNsThaiSmartPnd54Pp36: false,
        isTopserv: false,
        isFixedAssetCount: false,
        isLab: false,
        // 'YYYY-MM-DD' ตามปฏิทินไทย ใช้ได้ถึงสิ้นวันนั้น null = ไม่มีวันหมดอายุ
        isLabExpireDate: null,
        isLaoTaxRis: false,
      },
      originalProductList: {},
      productVersions: {},
      apAutomateTypes: [
        { text: "SAP B1", value: 1 },
        { text: "Express", value: 2 },
      ],
      etaxTypes: [
        { text: "— ไม่ระบุ —", value: null },
        { text: "SAP Business One", value: 1 },
        { text: "SAP Hana", value: 2 },
        { text: "Express", value: 3 },
        { text: "นำเข้าข้อมูล CSV", value: 4 },
        { text: "NetSuite", value: 5 },
      ],
      breadcrumbItems: [
        {
          text: "ตั้งค่า",
          disabled: false,
          to: { name: "Company.Settings" },
        },
        {
          text: "จัดการผลิตภัณฑ์",
          disabled: true,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      userCompany: (state) => state.user.userCompany,
      user: (state) => state.user.userData,
    }),
    companyId() {
      return this.$route.params.companyId;
    },
    currentHistory() {
      if (!this.versionDialogKey || !this.versionHistory[this.versionDialogKey]) return [];
      // Firestore เก็บเรียง 0=เก่าสุด → แสดงผล reverse ให้ใหม่สุดอยู่บน
      return [...this.versionHistory[this.versionDialogKey]].reverse();
    },
    isCompanySuspended() {
      return this.selectedCompany?.isSuspended === true;
    },
    isLabLicenseExpired() {
      return isLabLicenseExpired(this.productList);
    },
    labExpireHint() {
      if (!this.productList.isLabExpireDate) {
        return "ไม่กำหนดวันหมดอายุ — บริษัทนี้ใช้งาน AP Lab ได้ตลอด";
      }
      const days = labLicenseDaysLeft(this.productList.isLabExpireDate);
      if (days === null) return "รูปแบบวันที่ไม่ถูกต้อง";
      const until = formatLabExpireDate(this.productList.isLabExpireDate);
      if (days < 0) return `หมดอายุแล้วเมื่อ ${until} — ผู้ใช้เข้าบริษัทนี้ไม่ได้`;
      if (days === 0) return `ใช้งานได้ถึงสิ้นวันนี้ (${until})`;
      return `ใช้งานได้ถึงสิ้นวันที่ ${until} (อีก ${days} วัน)`;
    },
    suspendedAtText() {
      const ts = this.selectedCompany?.suspendedAt;
      if (!ts) return "";
      const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleString("th-TH");
    },
    activeProducts() {
      const active = {};
      Object.keys(this.productList).forEach((key) => {
        if (
          this.productList[key] === true &&
          key !== "isApAutomateType" &&
          key !== "isEtaxType"
        ) {
          active[key] = true;
        }
      });
      return active;
    },
  },
  watch: {
    productList: {
      handler() {
        this.checkForChanges();
      },
      deep: true,
    },
    'productList.isNsThaiSmartTax': function(newVal) {
      // When NS Thai Smart Tax is turned off, also turn off related settings
      if (!newVal) {
        if (this.productList.isNsThaiSmartTaxAverage) {
          this.productList.isNsThaiSmartTaxAverage = false;
        }
        if (this.productList.isNsThaiSmartPnd54Pp36) {
          this.productList.isNsThaiSmartPnd54Pp36 = false;
        }
      }
    },
  },
  created() {
    this.loadProductList();
  },
  methods: {
    async loadProductList() {
      try {
        this.loading = true;
        const doc = await fc.companies
          .doc(this.companyId)
          .collection("ProductList")
          .doc("PSC")
          .get();

        if (doc.exists) {
          this.productList = {
            ...this.productList,
            ...doc.data(),
          };
          this.originalProductList = JSON.parse(JSON.stringify(this.productList));
        }
      } catch (error) {
        console.error("Error loading product list:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      } finally {
        this.loading = false;
      }

      // โหลด version + history แยก — ถ้า fail ไม่กระทบ product list
      try {
        const [versionDoc, historyDoc] = await Promise.all([
          fc.db.collection("ProductVersions").doc("PSC").get(),
          fc.db.collection("ProductVersions").doc("History").get(),
        ]);

        const firestoreVersions = versionDoc.exists ? versionDoc.data() : {};
        if (historyDoc.exists) {
          this.versionHistory = historyDoc.data();
        }

        // Auto sync: เฉพาะ production เท่านั้น — ไม่ sync ตอนรัน dev server
        const isProduction = process.env.NODE_ENV === 'production';
        const updates = {};
        const historyUpdates = {};
        for (const key of Object.keys(configVersions)) {
          const codeVer = configVersions[key];
          const fsVer = firestoreVersions[key];

          if (!fsVer || codeVer.version !== fsVer.version) {
            updates[key] = {
              version: codeVer.version,
              updatedAt: codeVer.updatedAt,
            };
            // เพิ่ม history entry
            if (!this.versionHistory[key]) {
              this.$set(this.versionHistory, key, []);
            }
            this.versionHistory[key].push({
              version: codeVer.version,
              updatedAt: codeVer.updatedAt,
              note: codeVer.note || '',
              updatedBy: __VERSION_AUTHORS__[key] || this.user?.email || 'system (auto sync)',
            });
            historyUpdates[key] = this.versionHistory[key];
          }
        }

        if (isProduction && Object.keys(updates).length > 0) {
          await Promise.all([
            fc.db.collection("ProductVersions").doc("PSC").set(updates, { merge: true }),
            fc.db.collection("ProductVersions").doc("History").set(historyUpdates, { merge: true }),
          ]);
          this.productVersions = { ...firestoreVersions, ...updates };
        } else if (!isProduction) {
          // dev: ใช้ config file เป็น version แสดงผล แต่ไม่เขียน Firestore
          const merged = { ...firestoreVersions };
          for (const key of Object.keys(configVersions)) {
            merged[key] = {
              version: configVersions[key].version,
              updatedAt: configVersions[key].updatedAt,
            };
          }
          this.productVersions = merged;
        } else {
          this.productVersions = firestoreVersions;
        }
      } catch (error) {
        console.error("Error loading versions:", error);
        // fallback ใช้ config file
        const fallback = {};
        for (const key of Object.keys(configVersions)) {
          fallback[key] = {
            version: configVersions[key].version,
            updatedAt: configVersions[key].updatedAt,
          };
        }
        this.productVersions = fallback;
      }
    },
    isNewerVersion(a, b) {
      const pa = a.split('.').map(Number);
      const pb = b.split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if ((pa[i] || 0) > (pb[i] || 0)) return true;
        if ((pa[i] || 0) < (pb[i] || 0)) return false;
      }
      return false;
    },
    checkForChanges() {
      this.hasChanges = JSON.stringify(this.productList) !== JSON.stringify(this.originalProductList);
    },
    async saveChanges() {
      try {
        this.saving = true;
        await fc.companies
          .doc(this.companyId)
          .collection("ProductList")
          .doc("PSC")
          .set(this.productList, { merge: true });

        this.originalProductList = JSON.parse(JSON.stringify(this.productList));
        this.hasChanges = false;
        this.showSnackbar("บันทึกการเปลี่ยนแปลงเรียบร้อยแล้ว", "success");

        // Refresh the product list in the store
        this.$store.dispatch("companies/getCompanyProductList", {
          companyId: this.companyId,
        });
      } catch (error) {
        console.error("Error updating product:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error");
      } finally {
        this.saving = false;
      }
    },
    extendLabLicense(months) {
      const current = this.productList.isLabExpireDate;
      const today = bangkokToday();
      const base = current && current > today ? current : today;
      this.productList.isLabExpireDate = addMonthsToDateString(base, months);
    },
    cancelChanges() {
      this.productList = JSON.parse(JSON.stringify(this.originalProductList));
      this.hasChanges = false;
    },
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    getVersionText(key) {
      const info = this.productVersions[key];
      return info ? info.version : '1.0.0';
    },
    openVersionDialog(key) {
      this.versionDialogKey = key;
      this.versionDialogCurrent = this.getVersionText(key);
      this.versionDialogNote = '';
      this.showHistory = false;
      this.versionDialog = true;
    },
    bumpVersion(type) {
      const current = this.versionDialogCurrent || '1.0.0';
      const parts = current.split('.').map(Number);
      const [major, minor, patch] = [parts[0] || 1, parts[1] || 0, parts[2] || 0];

      if (type === 'major') {
        this.versionDialogCurrent = `${major + 1}.0.0`;
      } else if (type === 'minor') {
        const newMinor = minor + 1;
        if (newMinor >= 100) {
          this.showSnackbar('Minor version ต้องน้อยกว่า 100 กรุณา bump Major แทน', 'warning');
          return;
        }
        this.versionDialogCurrent = `${major}.${newMinor}.0`;
      } else {
        const newPatch = patch + 1;
        if (newPatch >= 1000) {
          this.showSnackbar('Patch version ต้องน้อยกว่า 1000 กรุณา bump Minor แทน', 'warning');
          return;
        }
        this.versionDialogCurrent = `${major}.${minor}.${newPatch}`;
      }
    },
    validateVersion(version) {
      const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
      if (!match) return 'รูปแบบ version ไม่ถูกต้อง (ต้องเป็น x.x.x)';
      const minor = parseInt(match[2]);
      const patch = parseInt(match[3]);
      if (minor >= 100) return 'Minor version ต้องน้อยกว่า 100';
      if (patch >= 1000) return 'Patch version ต้องน้อยกว่า 1000';
      return null;
    },
    async saveVersion() {
      const key = this.versionDialogKey;
      const newVersion = this.versionDialogCurrent.trim();
      const note = this.versionDialogNote.trim();

      if (!newVersion) return;

      const error = this.validateVersion(newVersion);
      if (error) {
        this.showSnackbar(error, 'warning');
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      this.$set(this.productVersions, key, {
        version: newVersion,
        updatedAt: today,
      });

      // เพิ่ม history entry
      if (!this.versionHistory[key]) {
        this.$set(this.versionHistory, key, []);
      }
      this.versionHistory[key].push({
        version: newVersion,
        updatedAt: today,
        note: note,
        updatedBy: this.user?.email || 'unknown',
      });

      try {
        await Promise.all([
          fc.db.collection("ProductVersions").doc("PSC").set(this.productVersions, { merge: true }),
          fc.db.collection("ProductVersions").doc("History").set(
            { [key]: this.versionHistory[key] },
            { merge: true }
          ),
        ]);

        this.versionDialog = false;
        this.showSnackbar(`${this.getProductLabel(key)} อัพเดทเป็น v${newVersion}`, "success");
      } catch (error) {
        console.error("Error saving version:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก version", "error");
      }
    },
    versionClass(key) {
      const info = this.productVersions[key];
      if (!info || !info.updatedAt) return 'version-old';
      const diffDays = (new Date() - new Date(info.updatedAt)) / (1000 * 60 * 60 * 24);
      return diffDays <= 7 ? 'version-new' : 'version-old';
    },
    openSuspendDialog() {
      this.suspendReason = "";
      this.suspendDialog = true;
    },
    openUnsuspendDialog() {
      this.unsuspendDialog = true;
    },
    async confirmSuspend() {
      const reason = (this.suspendReason || "").trim();
      if (!reason) {
        this.showSnackbar("กรุณาระบุเหตุผลในการปิดบริษัท", "warning");
        return;
      }
      try {
        this.suspending = true;
        await this.$store.dispatch("companies/suspendCompany", {
          companyId: this.companyId,
          reason,
          suspendedBy: this.user?.email || "unknown",
        });
        this.suspendDialog = false;
        this.suspendReason = "";
        this.showSnackbar("ปิดการใช้งานบริษัทเรียบร้อยแล้ว", "success");
      } catch (error) {
        console.error("Error suspending company:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการปิดบริษัท", "error");
      } finally {
        this.suspending = false;
      }
    },
    async confirmUnsuspend() {
      try {
        this.suspending = true;
        await this.$store.dispatch("companies/unsuspendCompany", {
          companyId: this.companyId,
          unsuspendedBy: this.user?.email || "unknown",
        });
        this.unsuspendDialog = false;
        this.showSnackbar("เปิดการใช้งานบริษัทเรียบร้อยแล้ว", "success");
      } catch (error) {
        console.error("Error unsuspending company:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการเปิดบริษัท", "error");
      } finally {
        this.suspending = false;
      }
    },
    getProductLabel(key) {
      const labels = {
        isEtax: "E-Tax",
        isPdfDoc: "AR Automation",
        isApAutomate: "AP Automation",
        isExchangeRate: "Exchange Rate",
        isNsThaiSmartTax: "NS Thai Smart Tax",
        isNsThaiSmartTaxAverage: "NS Tax Average",
        isNsThaiSmartPnd54Pp36: "ภ.ง.ด.54 / ภ.พ.36",
        isTopserv: "Topserv",
        isFixedAssetCount: "Fixed Asset Count",
        isLab: "AP Lab/Student Mode",
        isLaoTaxRis: "Lao TaxRIS",
      };
      return labels[key] || key;
    },
  },
};
</script>

<style lang="scss" scoped>
.history-list {
  max-height: 250px;
  overflow-y: auto;
}

.history-item {
  padding: 10px 12px;
  border-left: 3px solid #6ec39b;
  margin-bottom: 8px;
  background: #f9f9f9;
  border-radius: 0 6px 6px 0;
}

.history-version {
  font-weight: 700;
  font-family: monospace;
  font-size: 0.85rem;
  color: #333;
}

.version-input {
  ::v-deep input {
    text-align: center;
    font-size: 1.5rem;
    font-weight: 700;
    font-family: monospace;
  }
}

.version-badge {
  cursor: pointer;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.75em;
  margin-left: 6px;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

.version-new {
  color: #fff;
  background-color: #4caf50;
  font-weight: 600;
}

.version-old {
  color: #757575;
  background-color: #e0e0e0;
  font-weight: 400;
}

.product-switch-wrapper {
  transition: all 0.2s ease;
}

.danger-zone {
  background: #fff5f5;
  border: 1px solid #f3c4c4;
  border-radius: 10px;
  padding: 16px 20px;

  &__title {
    color: #c9242b;
    display: flex;
    align-items: center;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
  }
}

.additional-setting-item {
  min-height: 80px;
  display: flex;
  flex-direction: column;

  label {
    flex-shrink: 0;
  }
}

.save-button-wrapper {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 16px 0;
  margin: -16px -20px 0;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  z-index: 10;
}

.action-button {
  transition: opacity 0.2s ease !important;

  &.button-disabled {
    opacity: 0.4 !important;
  }

  &:not(.button-disabled) {
    opacity: 1 !important;
  }
}

// Override Vuetify's default disabled styles to keep the button visible
::v-deep .action-button.v-btn--disabled {
  opacity: 0.4 !important;
  color: inherit !important;

  &.v-btn--outlined {
    border-color: currentColor !important;
  }
}
</style>
